import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import BookingListing from './BookingListing';
import BookingCardView from './BookingCardView';
import BookingCalendarView from './BookingCalendarView';
import BookingFilters from './BookingFilters';
import BookingFilterPanel from './BookingFilterPanel';
import BookingExportMenu from './BookingExportMenu';
import BookingSummaryCards from './BookingSummaryCards';
import ConflictAlert from './ConflictAlert';
import ConflictResolutionModal from './ConflictResolutionModal';
import ManualBookingForm from './ManualBookingForm';
import BookingDetailsEditableView from './BookingDetailsEditableView';
import BookingInvoice from './BookingInvoice';
import Modal from '../Common/Modal';
import { Booking, BookingFilters as BookingFiltersType, BookingSummary } from '../../types/booking';
import { bookingService } from '../../services/bookingService';

interface BookingDashboardProps {
  onBookingSelect?: (booking: Booking) => void;
  onBookingEdit?: (booking: Booking) => void;
}

const BookingDashboard: React.FC<BookingDashboardProps> = ({
  onBookingSelect,
  onBookingEdit
}) => {
  // URL search params for highlighting
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightBookingId = searchParams.get('highlight');
  const highlightedRowRef = useRef<HTMLDivElement>(null);
  
  // Get current month and year
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth(); // 0-11
  const currentYear = currentDate.getFullYear();
  
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [summary, setSummary] = useState<BookingSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<BookingFiltersType>({});
  const [refreshKey, setRefreshKey] = useState(0);
  const [conflictedBookings, setConflictedBookings] = useState<Booking[]>([]);
  const [selectedConflictBooking, setSelectedConflictBooking] = useState<Booking | null>(null);
  const [showConflictResolution, setShowConflictResolution] = useState(false);
  const [showManualBookingForm, setShowManualBookingForm] = useState(false);
  const [selectedBookingForView, setSelectedBookingForView] = useState<Booking | null>(null);
  const [invoiceBooking, setInvoiceBooking] = useState<Booking | null>(null);
  const [highlightedBookingId, setHighlightedBookingId] = useState<string | null>(highlightBookingId);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalBookingsCount, setTotalBookingsCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  
  // View mode state
  const [viewMode, setViewMode] = useState<'table' | 'card' | 'calendar'>('table');
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  
  // Month and Year filters - default to current month/year
  const [selectedMonth, setSelectedMonth] = useState<number>(currentMonth);
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const [selectedDate, setSelectedDate] = useState<string>(''); // Specific date filter
  const [filterMode, setFilterMode] = useState<'month' | 'date'>('month'); // Toggle between month and date filtering
  
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  
  // Generate year options (current year and past 5 years, future 2 years)
  const years = Array.from({ length: 8 }, (_, i) => currentYear - 5 + i);

  const loadBookings = async () => {
    try {
      setLoading(true);
      setError(null);
      
      let startDate: Date;
      let endDate: Date;
      
      if (filterMode === 'date' && selectedDate) {
        // Filter by specific date - show bookings that overlap with this date
        const targetDate = new Date(selectedDate);
        startDate = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate(), 0, 0, 0);
        endDate = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate(), 23, 59, 59);
      } else {
        // Filter by month/year - show all bookings in the selected month
        startDate = new Date(selectedYear, selectedMonth, 1);
        endDate = new Date(selectedYear, selectedMonth + 1, 0, 23, 59, 59);
      }
      
      // Add date range to filters
      const dateFilters = {
        ...filters,
        startDate: startDate,
        endDate: endDate
      };
      
      const [bookingsResponse, summaryResponse] = await Promise.all([
        bookingService.getBookingsPage({
          ...dateFilters,
          page: currentPage,
          limit: pageSize,
          lightweight: true,
        }),
        bookingService.getBookingSummary()
      ]);
      
      if (bookingsResponse.success && bookingsResponse.data) {
        const pageData = bookingsResponse.data;
        setBookings(pageData.items || []);
        setTotalBookingsCount(pageData.total || 0);
        setTotalPages(pageData.totalPages || 1);

        if ((pageData.items || []).length === 0 && (pageData.total || 0) > 0 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        }

        // Filter out conflicted bookings for alerts
        const conflicted = (pageData.items || []).filter(booking => booking.hasConflict);
        setConflictedBookings(conflicted);
      } else {
        setError(bookingsResponse.error?.message || 'Failed to load bookings');
      }
      
      if (summaryResponse.success && summaryResponse.data) {
        setSummary(summaryResponse.data);
      }
    } catch (err) {
      setError('Failed to load booking data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [filters, selectedMonth, selectedYear, selectedDate, filterMode]);

  useEffect(() => {
    loadBookings();
  }, [filters, refreshKey, selectedMonth, selectedYear, selectedDate, filterMode, currentPage, pageSize]);

  // Handle highlight from URL params - scroll to and highlight the booking
  useEffect(() => {
    if (highlightBookingId && bookings.length > 0 && !loading) {
      // Find the booking in the current list
      const bookingExists = bookings.find(b => b._id === highlightBookingId);
      
      if (bookingExists) {
        // Set the highlighted booking
        setHighlightedBookingId(highlightBookingId);
        
        // Scroll to the booking after a short delay to ensure DOM is ready
        setTimeout(() => {
          const element = document.getElementById(`booking-row-${highlightBookingId}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 300);
        
        // Clear the highlight after 5 seconds
        setTimeout(() => {
          setHighlightedBookingId(null);
          // Remove the highlight param from URL
          searchParams.delete('highlight');
          setSearchParams(searchParams, { replace: true });
        }, 5000);
      } else {
        // Booking not in current view - clear filters to show all bookings
        setSelectedMonth(currentMonth);
        setSelectedYear(currentYear);
        setFilters({});
      }
    }
  }, [highlightBookingId, bookings, loading]);

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  const handleBookingUpdate = async (updatedBooking: Booking) => {
    console.log('🔄 handleBookingUpdate called with:', updatedBooking._id);
    
    // Update local state immediately for responsiveness
    setBookings(prev => 
      prev.map(booking => 
        booking._id === updatedBooking._id ? updatedBooking : booking
      )
    );
    
    // Update conflicted bookings list
    setConflictedBookings(prev => 
      prev.map(booking => 
        booking._id === updatedBooking._id ? updatedBooking : booking
      ).filter(booking => booking.hasConflict)
    );
    
    // Force a full refresh from the database to ensure data consistency
    console.log('🔄 Triggering full refresh from database...');
    await loadBookings();
    console.log('✅ Refresh complete');
  };

  const handleConflictResolve = (booking: Booking) => {
    setSelectedConflictBooking(booking);
    setShowConflictResolution(true);
  };

  const handleConflictResolution = async (resolution: any) => {
    try {
      if (selectedConflictBooking) {
        // Apply resolution and update booking
        await bookingService.resolveBookingConflict(selectedConflictBooking._id, {
          resolvedBy: resolution.resolvedBy,
          resolution: resolution.reason
        });
        
        // Remove from conflicted bookings
        setConflictedBookings(prev => 
          prev.filter(booking => booking._id !== selectedConflictBooking._id)
        );
        
        handleRefresh();
      }
    } catch (error) {
      console.error('Failed to resolve conflict:', error);
    } finally {
      setShowConflictResolution(false);
      setSelectedConflictBooking(null);
    }
  };

  const handleBookingSelect = async (booking: Booking) => {
    try {
      const fullBookingRes = await bookingService.getBookingById(booking._id);
      const fullBooking = fullBookingRes.success && fullBookingRes.data ? fullBookingRes.data : booking;
      setSelectedBookingForView(fullBooking);
      if (onBookingSelect) {
        onBookingSelect(fullBooking);
      }
    } catch (error) {
      setSelectedBookingForView(booking);
      if (onBookingSelect) {
        onBookingSelect(booking);
      }
    }
  };

  const handleInvoiceView = async (booking: Booking) => {
    try {
      const fullBookingRes = await bookingService.getBookingById(booking._id);
      const fullBooking = fullBookingRes.success && fullBookingRes.data ? fullBookingRes.data : booking;
      setInvoiceBooking(fullBooking);
    } catch (error) {
      setInvoiceBooking(booking);
    }
  };

  if (loading && bookings.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading bookings...</span>
      </div>
    );
  }

  if (error && bookings.length === 0) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center">
          <div className="text-red-800">
            <strong>Error:</strong> {error}
          </div>
          <button
            onClick={handleRefresh}
            className="ml-4 px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Enhanced Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white">Booking Management</h1>
            <p className="text-blue-100 mt-1">Manage and track all your bookings</p>
          </div>
          <div className="flex items-center gap-3">
            {/* View Mode Toggle */}
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-1 flex gap-1">
              <button
                onClick={() => setViewMode('table')}
                className={`px-3 py-2 rounded-md transition-all ${
                  viewMode === 'table'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-white hover:bg-white/20'
                }`}
                title="Table View"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </button>
              <button
                onClick={() => setViewMode('card')}
                className={`px-3 py-2 rounded-md transition-all ${
                  viewMode === 'card'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-white hover:bg-white/20'
                }`}
                title="Card View"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                </svg>
              </button>
              <button
                onClick={() => setViewMode('calendar')}
                className={`px-3 py-2 rounded-md transition-all ${
                  viewMode === 'calendar'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-white hover:bg-white/20'
                }`}
                title="Calendar View"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </button>
            </div>

            <button
              onClick={() => setShowFilterPanel(!showFilterPanel)}
              className="px-4 py-2 bg-white/10 backdrop-blur-sm text-white rounded-lg hover:bg-white/20 transition-all flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              Filters
            </button>

            <div className="bg-white/10 backdrop-blur-sm rounded-lg">
              <BookingExportMenu bookings={bookings} />
            </div>

            <button
              onClick={() => setShowManualBookingForm(true)}
              className="px-4 py-2 bg-white text-blue-600 rounded-lg hover:bg-blue-50 transition-all font-medium shadow-sm flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              New Booking
            </button>

            <button
              onClick={handleRefresh}
              className="p-2 bg-white/10 backdrop-blur-sm text-white rounded-lg hover:bg-white/20 transition-all"
              disabled={loading}
              title="Refresh"
            >
              <svg className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Month/Year and Date Filter */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="space-y-4">
          {/* Filter Mode Toggle */}
          <div className="flex items-center gap-4 border-b pb-3">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span className="text-sm font-medium text-gray-700">Filter by:</span>
            </div>
            
            <div className="flex gap-2">
              <button
                onClick={() => setFilterMode('month')}
                className={`px-4 py-2 text-sm rounded-md transition-colors ${
                  filterMode === 'month'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Month & Year
              </button>
              <button
                onClick={() => setFilterMode('date')}
                className={`px-4 py-2 text-sm rounded-md transition-colors ${
                  filterMode === 'date'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Specific Date
              </button>
            </div>
          </div>

          {/* Month/Year Filter */}
          {filterMode === 'month' && (
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-3">
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  {months.map((month, index) => (
                    <option key={index} value={index}>
                      {month}
                    </option>
                  ))}
                </select>

                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  {years.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>

                <button
                  onClick={() => {
                    setSelectedMonth(currentMonth);
                    setSelectedYear(currentYear);
                  }}
                  className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  title="Reset to current month"
                >
                  Current Month
                </button>
              </div>

              <div className="ml-auto text-sm text-gray-600">
                Showing: <span className="font-medium text-gray-900">{months[selectedMonth]} {selectedYear}</span>
                {selectedMonth === currentMonth && selectedYear === currentYear && (
                  <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full">Current</span>
                )}
              </div>
            </div>
          )}

          {/* Date Filter */}
          {filterMode === 'date' && (
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-3">
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                />

                <button
                  onClick={() => setSelectedDate(currentDate.toISOString().split('T')[0])}
                  className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  title="Set to today"
                >
                  Today
                </button>

                <button
                  onClick={() => {
                    const yesterday = new Date(currentDate);
                    yesterday.setDate(yesterday.getDate() - 1);
                    setSelectedDate(yesterday.toISOString().split('T')[0]);
                  }}
                  className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  title="Set to yesterday"
                >
                  Yesterday
                </button>

                <button
                  onClick={() => {
                    const tomorrow = new Date(currentDate);
                    tomorrow.setDate(tomorrow.getDate() + 1);
                    setSelectedDate(tomorrow.toISOString().split('T')[0]);
                  }}
                  className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  title="Set to tomorrow"
                >
                  Tomorrow
                </button>
              </div>

              <div className="ml-auto text-sm text-gray-600">
                {selectedDate ? (
                  <>
                    Showing: <span className="font-medium text-gray-900">
                      {new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', { 
                        weekday: 'short', 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </span>
                    {selectedDate === currentDate.toISOString().split('T')[0] && (
                      <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded-full">Today</span>
                    )}
                  </>
                ) : (
                  <span className="text-gray-400">Select a date to filter</span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <BookingSummaryCards 
          summary={summary} 
          onCardClick={(filter: Partial<BookingFiltersType>) => setFilters(prev => ({ ...prev, ...filter }))}
        />
      )}

      {/* Filters */}
      <BookingFilters
        filters={filters}
        onFiltersChange={setFilters}
        bookings={bookings}
      />

      {/* Error Display */}
      {error && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="text-yellow-800">
            <strong>Warning:</strong> {error}
          </div>
        </div>
      )}

      {/* Filter Panel Sidebar */}
      {showFilterPanel && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          <div className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowFilterPanel(false)}></div>
          <div className="fixed inset-y-0 right-0 max-w-md w-full">
            <div className="h-full overflow-y-auto">
              <BookingFilterPanel
                filters={filters}
                onFiltersChange={(newFilters) => {
                  setFilters(newFilters);
                  setShowFilterPanel(false);
                }}
                onClose={() => setShowFilterPanel(false)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Active Filter Chips */}
      {Object.values(filters).some(v => v !== undefined && v !== '') && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-gray-600 font-medium">Active Filters:</span>
          {filters.status && (
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
              Status: {filters.status}
              <button onClick={() => setFilters({ ...filters, status: undefined })} className="hover:text-blue-900">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </span>
          )}
          {filters.paymentStatus && (
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
              Payment: {filters.paymentStatus}
              <button onClick={() => setFilters({ ...filters, paymentStatus: undefined })} className="hover:text-green-900">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </span>
          )}
          {filters.search && (
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm">
              Search: "{filters.search}"
              <button onClick={() => setFilters({ ...filters, search: undefined })} className="hover:text-purple-900">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </span>
          )}
          <button
            onClick={() => setFilters({})}
            className="text-sm text-gray-600 hover:text-gray-900 underline"
          >
            Clear all
          </button>
        </div>
      )}

      {/* Booking Views */}
      {viewMode === 'table' && (
        <BookingListing
          bookings={bookings}
          onBookingSelect={handleBookingSelect}
          onBookingUpdate={handleBookingUpdate}
          onInvoiceView={handleInvoiceView}
          loading={loading}
          highlightedBookingId={highlightedBookingId}
        />
      )}

      {viewMode === 'card' && (
        <BookingCardView
          bookings={bookings}
          onBookingSelect={handleBookingSelect}
          onBookingUpdate={handleBookingUpdate}
          onInvoiceView={handleInvoiceView}
          loading={loading}
        />
      )}

      {(viewMode === 'table' || viewMode === 'card') && totalPages > 1 && (
        <div className="bg-white rounded-lg shadow p-4 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            Showing page <span className="font-medium">{currentPage}</span> of{' '}
            <span className="font-medium">{totalPages}</span> ({totalBookingsCount} total bookings)
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage <= 1}
              className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={currentPage >= totalPages}
              className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {viewMode === 'calendar' && (
        <BookingCalendarView
          bookings={bookings}
          onBookingSelect={handleBookingSelect}
          onDateSelect={(date) => {
            setSelectedDate(date.toISOString().split('T')[0]);
            setFilterMode('date');
          }}
        />
      )}

      {/* View/Edit Booking Modal */}
      {selectedBookingForView && (
        <Modal
          isOpen={true}
          onClose={() => setSelectedBookingForView(null)}
          title={`Booking Details - ${selectedBookingForView.bookingReference}`}
          size="xl"
        >
          <BookingDetailsEditableView 
            booking={selectedBookingForView}
            onClose={() => setSelectedBookingForView(null)}
            onUpdate={(updatedBooking) => {
              handleBookingUpdate(updatedBooking);
              setSelectedBookingForView(updatedBooking); // Keep modal open with updated data
            }}
          />
        </Modal>
      )}

      {/* Invoice Modal */}
      {invoiceBooking && (
        <BookingInvoice 
          booking={invoiceBooking} 
          onClose={() => setInvoiceBooking(null)}
          onSave={async () => {
            // Refresh bookings after save - but DON'T close the invoice
            console.log('🔄 Invoice saved, refreshing bookings...');
            await loadBookings();
            console.log('✅ Bookings refreshed after invoice save');
            // Invoice stays open - user can close it manually when done
          }}
        />
      )}

      {/* Conflict Resolution Modal */}
      {selectedConflictBooking && (
        <ConflictResolutionModal
          booking={selectedConflictBooking}
          conflictingBookings={[]} // This would be populated with actual conflicting bookings
          onResolve={handleConflictResolution}
          onCancel={() => {
            setShowConflictResolution(false);
            setSelectedConflictBooking(null);
          }}
          isOpen={showConflictResolution}
        />
      )}

      {/* Manual Booking Form */}
      <ManualBookingForm
        isOpen={showManualBookingForm}
        onClose={() => setShowManualBookingForm(false)}
        onSuccess={(booking) => {
          handleRefresh();
          setShowManualBookingForm(false);
        }}
      />
    </div>
  );
};

export default BookingDashboard;
